import React, { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowRight01Icon,
  AlertCircleIcon,
  InformationCircleIcon,
  CheckmarkCircle01Icon,
  Copy01Icon,
  FlashIcon
} from '@hugeicons/core-free-icons';
import ResponsiveModal from './ResponsiveModal';
import Button from './Button';
import { api } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';

const CreateOrderBucketModal = ({ isOpen, onClose, onOrderCreated, agentBanks = [] }) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [createOrderStep, setCreateOrderStep] = useState(1);
  const [showCopied, setShowCopied] = useState(false);
  const [newestLink, setNewestLink] = useState(null);
  const [orderFormData, setOrderFormData] = useState({
    transaction_type: 'deposit',
    current_rate: '',
    spread: '',
    total_amount_usd: '',
    min_amount_usd: '',
    bank_account_id: '',
    cashback_rate: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateOrderLink = async () => {
    try {
      const response = await api.post('/agent/orders', {
        transaction_type: orderFormData.transaction_type,
        current_rate: parseFloat(orderFormData.current_rate),
        spread: parseFloat(orderFormData.spread),
        total_amount_usd: parseFloat(orderFormData.total_amount_usd),
        min_amount_usd: parseFloat(orderFormData.min_amount_usd),
        bank_account_id: orderFormData.bank_account_id || null,
        cashback_rate: parseFloat(orderFormData.cashback_rate) || 0,
      });

      const link = response.data?.data?.link || response.data?.link;
      if (link) {
        setNewestLink({ url: link });
        if (onOrderCreated) {
          onOrderCreated(response.data);
        }
        setCreateOrderStep(1);
      }
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to create order bucket', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleClose = () => {
    setCreateOrderStep(1);
    setOrderFormData({
      transaction_type: 'deposit',
      current_rate: '',
      spread: '',
      total_amount_usd: '',
      min_amount_usd: '',
      bank_account_id: '',
      cashback_rate: '',
    });
    setNewestLink(null);
    onClose();
  };

  return (
    <>
      {/* Create Order Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Create Order Bucket (Step ${createOrderStep}/4)`}
      >
        <div className="py-4 space-y-6">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${createOrderStep >= s ? 'bg-soft-green' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
            ))}
          </div>

          {createOrderStep === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Transaction Type</label>
                <select name="transaction_type" value={orderFormData.transaction_type} onChange={handleInputChange} className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white">
                  <option value="deposit">Deposit (Client pays NGN)</option>
                  <option value="withdrawal">Withdrawal (Agent pays NGN)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Rate (₦/$)</label>
                  <input name="current_rate" type="text" inputMode="decimal" required value={orderFormData.current_rate} onChange={handleInputChange} placeholder="1650" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Spread (₦)</label>
                  <input name="spread" type="text" inputMode="decimal" required value={orderFormData.spread} onChange={handleInputChange} placeholder="40" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
                </div>
              </div>
              {orderFormData.transaction_type === 'deposit' && (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                  <label className="block text-[10px] font-black text-soft-green uppercase tracking-widest mb-2 px-1">Cashback Share (%)</label>
                  <div className="relative">
                    <input name="cashback_rate" type="text" inputMode="decimal" value={orderFormData.cashback_rate} onChange={handleInputChange} placeholder="5" className="w-full pl-4 pr-12 py-4 bg-soft-green/5 dark:bg-soft-green/10 border-2 border-soft-green/20 rounded-2xl outline-none font-black text-xl text-soft-green placeholder:text-soft-green/30" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-soft-green">%</span>
                  </div>
                  <p className="text-[9px] font-bold text-muted-gray mt-2 px-1 leading-tight">
                    Percentage of your spread (₦{orderFormData.spread || 0}) given back to clients.
                    {orderFormData.spread && orderFormData.cashback_rate && (
                      <span className="text-soft-green ml-1">Example: ₦{((parseFloat(orderFormData.spread) * parseFloat(orderFormData.cashback_rate)) / 100).toFixed(2)} per USD.</span>
                    )}
                  </p>
                </div>
              )}
              <Button onClick={() => setCreateOrderStep(2)} className="w-full py-4 rounded-2xl mt-4 font-black">Next: Set Limits <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
            </div>
          )}

          {createOrderStep === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Max Capacity ($)</label>
                <input name="total_amount_usd" type="text" inputMode="decimal" required value={orderFormData.total_amount_usd} onChange={handleInputChange} placeholder="5000" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-black text-2xl text-charcoal dark:text-white" />
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[1000, 2000, 5000, 10000].map((cap) => (
                    <button
                      key={cap}
                      type="button"
                      onClick={() => setOrderFormData({...orderFormData, total_amount_usd: cap.toString()})}
                      className="px-3 py-1.5 bg-soft-green/10 hover:bg-soft-green/20 text-soft-green rounded-lg text-xs font-black transition-all"
                    >
                      ${cap.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Min Order ($)</label>
                <input name="min_amount_usd" type="text" inputMode="decimal" required value={orderFormData.min_amount_usd} onChange={handleInputChange} placeholder="10" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white focus:ring-2 focus:ring-soft-green" />
                <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-800/30 flex gap-3">
                  <HugeiconsIcon icon={AlertCircleIcon} size={18} className="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest mb-1">Strict Transfer Policy</p>
                    <p className="text-[11px] font-bold text-red-700 dark:text-red-300 leading-relaxed">
                      {orderFormData.transaction_type === 'deposit'
                        ? "Kindly enter the minimum amount ($) YOU can transfer via Deriv. You will not be able to release funds smaller than your Payment Agent's Account minimum transfer limit. Your account will be suspended if you set this incorrectly."
                        : "Kindly enter the minimum amount ($) YOUR CLIENT can transfer via Deriv. They will not be able to send you funds smaller than your Payment Agent's Account minimum transfer limit. Your account will be suspended if you set this incorrectly."}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setCreateOrderStep(1)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={() => setCreateOrderStep(3)} className="flex-1 py-4 rounded-2xl font-black">Next: Bank <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
              </div>
            </div>
          )}

          {createOrderStep === 3 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              {orderFormData.transaction_type === 'deposit' ? (
                <>
                  <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Select Receiving Bank</label>
                  {agentBanks.length === 0 ? (
                    <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl text-center border border-red-100 dark:border-red-800/30">
                      <p className="text-sm text-red-600 dark:text-red-400 font-bold mb-4">Add a bank in your profile first.</p>
                      <Button onClick={() => navigate('/profile')} variant="secondary" className="text-xs py-2">Go to Profile</Button>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {agentBanks.map(bank => (
                        <div key={bank.id} onClick={() => setOrderFormData({...orderFormData, bank_account_id: bank.id})} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${orderFormData.bank_account_id === bank.id ? 'border-soft-green bg-soft-green/5' : 'border-light-gray/20 dark:border-gray-700'}`}>
                          <p className="font-black text-charcoal dark:text-white text-sm">{bank.bank_name}</p>
                          <p className="text-[10px] text-muted-gray font-bold">{bank.account_number} • {bank.account_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-blue-50 dark:bg-blue-900/10 p-8 rounded-3xl text-center border border-blue-100 dark:border-blue-800/30">
                  <HugeiconsIcon icon={InformationCircleIcon} size={48} className="text-blue-500 mx-auto mb-4" />
                  <h3 className="text-lg font-black text-blue-800 dark:text-blue-200">Withdrawal Order</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">Client bank details will appear upon order.</p>
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setCreateOrderStep(2)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={() => setCreateOrderStep(4)} disabled={orderFormData.transaction_type === 'deposit' && !orderFormData.bank_account_id} className="flex-1 py-4 rounded-2xl font-black">Next: Review <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
              </div>
            </div>
          )}

          {createOrderStep === 4 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-soft-green/5 dark:bg-soft-green/10 p-8 rounded-[2.5rem] border border-soft-green/20 text-center">
                <p className="text-muted-gray text-[10px] font-black uppercase tracking-[0.2em] mb-4">Order Summary</p>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center text-xs font-bold text-muted-gray">
                    <span>Maximum Capacity</span>
                    <span className="text-charcoal dark:text-white">${parseFloat(orderFormData.total_amount_usd || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-bold text-muted-gray">
                    <span>Minimum Per Order</span>
                    <span className="text-charcoal dark:text-white">${parseFloat(orderFormData.min_amount_usd || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-bold text-muted-gray border-t border-soft-green/10 pt-2 mt-2">
                    <span>Exchange Rate</span>
                    <span className="text-soft-green font-black">₦{parseFloat(orderFormData.current_rate || 0).toLocaleString()}/$</span>
                  </div>
                  {orderFormData.transaction_type === 'deposit' && (
                    <div className="flex justify-between items-center text-xs font-bold text-muted-gray pt-2">
                      <span>Cashback Share</span>
                      <span className="text-purple-500 font-black">{orderFormData.cashback_rate || 0}%</span>
                    </div>
                  )}
                </div>
                <p className="mt-6 text-[10px] font-black text-soft-green uppercase tracking-widest bg-soft-green/10 inline-block px-4 py-1 rounded-full">{orderFormData.transaction_type}</p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setCreateOrderStep(3)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={handleCreateOrderLink} className="flex-[2] py-4 rounded-2xl font-black shadow-float-lg">Launch Bucket</Button>
              </div>
            </div>
          )}
        </div>
      </ResponsiveModal>

      {/* Success Modal */}
      <ResponsiveModal
        isOpen={!!newestLink}
        onClose={() => { setNewestLink(null); setCreateOrderStep(1); }}
        title="Bucket Link Live"
      >
        <div className="py-6 text-center">
          <div className="bg-soft-green/10 p-6 rounded-[2rem] inline-block mb-6">
            <HugeiconsIcon icon={FlashIcon} size={48} className="text-soft-green" />
          </div>
          <h3 className="text-2xl font-black text-charcoal dark:text-white mb-4">Bucket Ready</h3>
          <p className="text-muted-gray mb-8 px-4 text-sm font-medium leading-relaxed">
            Share this link. Clients can use it multiple times until your <b>${orderFormData.total_amount_usd}</b> capacity is filled.
          </p>
          <div className="bg-off-white dark:bg-gray-800 p-4 rounded-2xl border border-light-gray/20 dark:border-gray-700/50 font-mono text-sm mb-8 break-all text-charcoal dark:text-white shadow-inner mx-4">
            {newestLink?.url}
          </div>
          <div className="flex flex-col gap-3 px-4">
            <Button onClick={() => copyToClipboard(newestLink?.url)} className="w-full py-4 rounded-2xl gap-2 font-black shadow-lg">
              <HugeiconsIcon icon={showCopied ? CheckmarkCircle01Icon : Copy01Icon} size={20} />
              {showCopied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button variant="secondary" onClick={() => { setNewestLink(null); setCreateOrderStep(1); }} className="w-full py-4 rounded-2xl font-black">Done</Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default CreateOrderBucketModal;
